// CoverBlockValue.jsx

import React, { useEffect, useState } from 'react';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './CoverBlockValue.css';

function normalizeString(value) {
	return String(value || '').trim();
}

function getNodeCoverRawValue(node) {
	const coverUrl = normalizeString(node?.coverUrl);
	const coverStoragePath = normalizeString(node?.coverStoragePath);
	return coverUrl || coverStoragePath || '';
}

/**
 * CoverBlockValue
 *
 * Renders the anchor node's cover photo as a full-width hero image.
 *
 * @param {object|null} node - The node providing the cover photo.
 * @param {boolean} showEmptyState - Whether to render a placeholder when no cover exists.
 *
 * @returns {ReactNode} The rendered cover image or empty state.
 */
const CoverBlockValue = React.memo(({ node = null, showEmptyState = false }) => {

	// ----- STATE ----- //

	const [resolvedImageUrl, setResolvedImageUrl] = useState('');
	const rawValue = getNodeCoverRawValue(node);

	// ----- EFFECTS ----- //

	useEffect(() => {
		let cancelled = false;

		if (!rawValue) {
			setResolvedImageUrl('');
			return undefined;
		}

		if (rawValue.startsWith('blob:') || rawValue.startsWith('data:')) {
			setResolvedImageUrl(rawValue);
			return undefined;
		}

		// Public pages pre-sign cover URLs server-side. Passing coverStoragePath here
		// would force a client re-sign via the media gate, which fails for anonymous viewers.
		const shouldResolveFromStoragePath = !rawValue.includes('://');

		(async () => {
			try {
				const nextUrl = await resolveImageUrl({
					nodeKey: node?.key || null,
					storagePath: shouldResolveFromStoragePath
						? (node?.coverStoragePath || null)
						: null,
					url: rawValue,
				});

				if (!cancelled) {
					setResolvedImageUrl(typeof nextUrl === 'string' ? nextUrl.trim() : '');
				}
			} catch (_error) {
				if (!cancelled) {
					setResolvedImageUrl('');
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [node?.coverStoragePath, node?.key, rawValue]);

	if (resolvedImageUrl) {
		return (
			<div className="cover-block-value-container">
				{/* IMAGE */}
				<img
					className="cover-block-value-image"
					alt={normalizeString(node?.title) || 'Cover photo'}
					src={resolvedImageUrl}
				/>
			</div>
		);
	}

	if (!showEmptyState) {
		return null;
	}

	// ----- RENDER ----- //

	return (
		<div className="cover-block-value-container">
			{/* EMPTY */}
			<div
				className="cover-block-value-empty"
			>
				Cover photo will be displayed here when added.
			</div>
		</div>
	);
});

// Dev Tools
CoverBlockValue.displayName = 'CoverBlockValue';

export default CoverBlockValue;
